import { useLazyLoadQuery } from 'react-relay';
import GET_FORUM_CATEGORIES_QUERY, {
  type GetForumCategoriesQuery,
} from '../../graphql/__generated__/GetForumCategoriesQuery.graphql';

const useForumCategories = () => {
  const forumCategoriesData = useLazyLoadQuery<GetForumCategoriesQuery>(
    GET_FORUM_CATEGORIES_QUERY,
    {},
    { fetchPolicy: 'store-or-network' },
  );

  const results = forumCategoriesData?.unified?.ai?.getForumCategories;

  // Check if we got a successful result with categories
  if (results?.__typename === 'UnifiedAiCategoriesResult') {
    const normalizedFilterOptions = normalizeCategoriesToFilterOptions(results.categories);

    return {
      results,
      normalizedFilterOptions,
    };
  }

  // Return null for errors or other cases
  return {
    results: null,
    normalizedFilterOptions: [],
  };
};

export default useForumCategories;

// Define how we want to group categories (same as original mock data)
const CATEGORY_GROUPS = {
  'Jira Products': [
    'Jira',
    'Advanced planning',
    'Jira Mobile Apps',
    'Jira Service Management',
    'Jira Product Discovery',
    'Jira Align',
  ],
  'Collaboration Tools': ['Confluence', 'Trello'],
  'Support and Operations': ['Assist', 'Opsgenie', 'Statuspage'],
  'Development Tools': ['Bitbucket', 'Pipelines', 'Bamboo', 'Fisheye/Crucible', 'Sourcetree'],
  'Platform and Security': [
    'Atlassian Home',
    'Atlassian Guard',
    'Atlassian Account',
    'Atlassian Automation',
    'Atlassian Platform',
    'Atlassian Analytics',
  ],
  'Community and Events': ['Community Announcements', 'Community Events'],
  Groups: [
    'Focus',
    'Talent',
    'Featured Groups',
    'Community Groups',
    'Product Groups',
    'Product Feedback Groups',
    'Industry Groups',
    'Language Groups',
  ],
};

type Category = {
  id: string;
  name: string;
  is_active: boolean;
};

// Normalization function to convert categories to filter options
const normalizeCategoriesToFilterOptions = (categories: readonly Category[]) => {
  if (!categories) {
    return [];
  }

  // Create a simple map: category name -> category id
  const categoryNameToId: Record<string, string> = {};
  categories
    .filter((category) => category.is_active)
    .forEach((category) => {
      categoryNameToId[category.name] = category.id;
    });

  // Build the filter options using our predefined groups
  const filterOptions = [];

  for (const [groupName, categoryNames] of Object.entries(CATEGORY_GROUPS)) {
    // Find which categories from this group exist in our data
    const existingCategories = categoryNames
      .filter((name) => categoryNameToId[name]) // Only include categories that exist
      .map((name) => ({
        label: name,
        value: categoryNameToId[name],
      }));

    // Only add this group if it has at least one category
    if (existingCategories.length > 0) {
      filterOptions.push({
        label: groupName,
        options: existingCategories,
      });
    }
  }

  return filterOptions;
};
